import React, {useEffect, useId, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, Stack, useBreakpointValue} from '@chakra-ui/react'
import {Component} from '@salesforce/commerce-sdk-react/components'
import PDCTitle from '../../../components/pdc-title'
import {DYNAMIC_IMAGE_WIDTHS} from '../../../constants.js'
import usePDCResponsiveElement from '../../../hooks/use-pdc-responsive-element.js'
import PDCImage from '../../../components/pdc-image/index.jsx'

/**
 * PDCHomeAboutPush is a responsive React component designed for the Page Designer Canvas (PDC) home/about push section.
 * It renders a visually rich, flexible layout that combines a title, subtitle, description, and up to three images,
 * supporting both mobile and desktop breakpoints. The component dynamically selects images based on the current
 * viewport size and can render additional custom components provided via the `regions` prop.
 *
 * Props:
 * @param {string} title - The main title text for the section.
 * @param {string|number} titleHeadingLevel - The heading level for the title (e.g., 'h1', 'h2', etc.).
 * @param {string} titleFontColor - The color of the title text.
 * @param {string} supTitle - The subtitle or descriptor HTML string.
 * @param {string} desc - The description HTML string for the section.
 * @param {string} backgroundColor - The background color of the section.
 * @param {string} textColor - The color of the text content.
 * @param {string} smallFirstImage - URL for the first image at the small breakpoint.
 * @param {string} mediumFirstImage - URL for the first image at the medium breakpoint.
 * @param {string} largeFirstImage - URL for the first image at the large breakpoint.
 * @param {string} xlargeFirstImage - URL for the first image at the xlarge breakpoint.
 * @param {string} xxlargeFirstImage - URL for the first image at the xxlarge breakpoint.
 * @param {string} firstImageTitle - Title attribute for the first image.
 * @param {string} firstImageAlt - Alt text for the first image.
 * @param {string} smallSecondImage - URL for the second image at the small breakpoint.
 * @param {string} mediumSecondImage - URL for the second image at the medium breakpoint.
 * @param {string} largeSecondImage - URL for the second image at the large breakpoint.
 * @param {string} xlargeSecondImage - URL for the second image at the xlarge breakpoint.
 * @param {string} xxlargeSecondImage - URL for the second image at the xxlarge breakpoint.
 * @param {string} secondImageTitle - Title attribute for the second image.
 * @param {string} secondImageAlt - Alt text for the second image.
 * @param {string} smallThirdImage - URL for the third image at the small breakpoint.
 * @param {string} mediumThirdImage - URL for the third image at the medium breakpoint.
 * @param {string} largeThirdImage - URL for the third image at the large breakpoint.
 * @param {string} xlargeThirdImage - URL for the third image at the xlarge breakpoint.
 * @param {string} xxlargeThirdImage - URL for the third image at the xxlarge breakpoint.
 * @param {string} thirdImageTitle - Title attribute for the third image.
 * @param {string} thirdImageAlt - Alt text for the third image.
 * @param {Array} regions - Array of region objects, each containing components to render as a list.
 * @param {Object} custom - Custom image overrides for each breakpoint.
 * @param {number} displayPriority - Priority for image loading/display.
 *
 * The component leverages responsive hooks and layout primitives to ensure optimal display across devices,
 * and supports dynamic content injection via the `regions` prop. It is suitable for use in marketing or
 * informational landing pages where a combination of text and imagery is required.
 */
export const PDCHomeAboutPush = ({
    title,
    titleHeadingLevel,
    titleFontColor,
    supTitle,
    desc,
    backgroundColor,
    textColor,
    smallFirstImage,
    mediumFirstImage,
    largeFirstImage,
    xlargeFirstImage,
    xxlargeFirstImage,
    firstImageTitle,
    firstImageAlt,
    smallSecondImage,
    mediumSecondImage,
    largeSecondImage,
    xlargeSecondImage,
    xxlargeSecondImage,
    secondImageTitle,
    secondImageAlt,
    smallThirdImage,
    mediumThirdImage,
    largeThirdImage,
    xlargeThirdImage,
    xxlargeThirdImage,
    thirdImageTitle,
    thirdImageAlt,
    regions,
    custom,
    displayPriority
}) => {
    let components = []
    if (regions) {
        components = regions[0].components || []
    }

    const titleId = useId()
    const [firstImageHeight, setFirstImageHeight] = useState(0)
    const [secondImageHeight, setSecondImageHeight] = useState(0)

    // Get current images based on breakpoint
    const currentFirstImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallFirstImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumFirstImage,
            largeEl: custom?.largeImage?.disBaseLink || largeFirstImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeFirstImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeFirstImage
        }),
        {
            fallback: undefined
        }
    )
    const currentSecondImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallSecondImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumSecondImage,
            largeEl: custom?.largeImage?.disBaseLink || largeSecondImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeSecondImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeSecondImage
        }),
        {
            fallback: undefined
        }
    )
    const currentThirdImage = useBreakpointValue(
        usePDCResponsiveElement({
            smallEl: custom?.smallImage?.disBaseLink || smallThirdImage,
            mediumEl: custom?.mediumImage?.disBaseLink || mediumThirdImage,
            largeEl: custom?.largeImage?.disBaseLink || largeThirdImage,
            xlargeEl: custom?.xlargeImage?.disBaseLink || xlargeThirdImage,
            xxlargeEl: custom?.xxlargeImage?.disBaseLink || xxlargeThirdImage
        }),
        {
            fallback: undefined
        }
    )

    return (
        <Box
            as="section"
            aria-describedby={titleId}
            display="flex"
            flexDirection={['column', 'column', 'row']}
            justifyContent="space-between"
            alignItems="stretch"
            width="100%"
            backgroundColor={backgroundColor}
            color={textColor}
            py={['100px', null, '120px']}
            px={['10px', null, '20px']}
            position={'relative'}
            height={[
                'auto',
                null,
                firstImageHeight > 0 && secondImageHeight > 0
                    ? `${firstImageHeight + secondImageHeight + 271}px`
                    : '100vh'
            ]}
        >
            <Flex
                flexDirection="column"
                justifyContent={'flex-start'}
                alignItems={'flex-start'}
                width={['100%', null, '50%']}
                mr={['auto', null, '60px']}
            >
                <Text
                    dangerouslySetInnerHTML={{
                        __html: supTitle
                    }}
                    variant="descriptor"
                    color={textColor ? textColor : 'fullBlack'}
                    mb={['24px', '24px', '34px']}
                />
                <PDCTitle
                    title={title}
                    titleFontColor={titleFontColor}
                    titleHeadingLevel={titleHeadingLevel}
                    txtHorizontalPos={'left'}
                    textColor={textColor}
                    id={titleId}
                    mb={['24px', '24px', '34px']}
                    size={['5xl', null, null, '6xl']}
                />
                <Flex>
                    <Box width={['auto', null, 'calc(32.85vw - 40px)']}>
                        <Text
                            dangerouslySetInnerHTML={{
                                __html: desc
                            }}
                            variant="bodyLarge2"
                            textAlign={'left'}
                        />

                        {components && (
                            <Stack
                                as="ul"
                                listStyleType="none"
                                pt={['24px', '24px', '34px']}
                                width={['auto', null, '304px']}
                                gap={'16px'}
                            >
                                {components.map((component, index) => {
                                    component.backgroundColor = backgroundColor
                                    component.textColor = textColor
                                    component.width = 'auto'
                                    return (
                                        <li key={component?.id || index}>
                                            <Component component={component} />
                                        </li>
                                    )
                                })}
                            </Stack>
                        )}
                    </Box>
                    <Box display={['block', null, 'none']}>
                        <ImageContainer
                            image={currentFirstImage}
                            displayPriority={displayPriority}
                            imageAlt={firstImageAlt}
                            imageTitle={firstImageTitle}
                            title={title}
                            ratioMobile={'125 / 191'}
                            ratioDesktop={'146 / 227'}
                            width="31vw"
                            ml="16px"
                        />
                    </Box>
                </Flex>
            </Flex>
            <Box display={['none', null, 'block']} width="44.7vw">
                <Flex>
                    <ImageContainer
                        image={currentFirstImage}
                        displayPriority={displayPriority}
                        imageAlt={firstImageAlt}
                        imageTitle={firstImageTitle}
                        title={title}
                        ratioMobile={'125 / 191'}
                        ratioDesktop={'146 / 227'}
                        onLoad={setFirstImageHeight}
                        width={'calc(50% - 30px)'}
                    />
                    <ImageContainer
                        image={currentThirdImage}
                        displayPriority={displayPriority}
                        imageAlt={thirdImageAlt}
                        imageTitle={thirdImageTitle}
                        title={title}
                        ratioMobile={'125 / 191'}
                        ratioDesktop={'146 / 227'}
                        width={'calc(50% - 30px)'}
                        transform="translateY(50%)"
                        ml="60px"
                    />
                </Flex>
            </Box>
            <ImageContainer
                image={currentSecondImage}
                displayPriority={displayPriority}
                imageAlt={secondImageAlt}
                imageTitle={secondImageTitle}
                title={title}
                ratioMobile={'13 / 9'}
                ratioDesktop={'13 / 9'}
                width={['79vw', null, '34.3vw']}
                mt={['50px', null, 0]}
                position={['relative', null, 'absolute']}
                top={['auto', null, firstImageHeight > 0 ? `${firstImageHeight + 193}px` : '45vw']}
                left={['auto', null, '50%']}
                transform={['none', null, 'translateX(-50%)']}
                mx={['0', null, 'auto']}
                onLoad={setSecondImageHeight}
            />
        </Box>
    )
}

PDCHomeAboutPush.propTypes = {
    title: PropTypes.string.isRequired,
    titleHeadingLevel: PropTypes.string,
    titleFontColor: PropTypes.string,
    supTitle: PropTypes.string,
    desc: PropTypes.string,
    backgroundColor: PropTypes.string,
    textColor: PropTypes.string,
    smallFirstImage: PropTypes.object.isRequired,
    mediumFirstImage: PropTypes.object,
    largeFirstImage: PropTypes.object,
    xlargeFirstImage: PropTypes.object,
    xxlargeFirstImage: PropTypes.object,
    firstImageTitle: PropTypes.string,
    firstImageAlt: PropTypes.string,
    smallSecondImage: PropTypes.object.isRequired,
    mediumSecondImage: PropTypes.object,
    largeSecondImage: PropTypes.object,
    xlargeSecondImage: PropTypes.object,
    xxlargeSecondImage: PropTypes.object,
    secondImageTitle: PropTypes.string,
    secondImageAlt: PropTypes.string,
    smallThirdImage: PropTypes.object.isRequired,
    mediumThirdImage: PropTypes.object,
    largeThirdImage: PropTypes.object,
    xlargeThirdImage: PropTypes.object,
    xxlargeThirdImage: PropTypes.object,
    thirdImageTitle: PropTypes.string,
    thirdImageAlt: PropTypes.string,
    custom: PropTypes.object,
    regions: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            components: PropTypes.arrayOf(
                PropTypes.shape({
                    data: PropTypes.object,
                    id: PropTypes.string,
                    typeId: PropTypes.string
                })
            )
        })
    ),
    displayPriority: PropTypes.bool
}

/**
 * ImageContainer is a React functional component that renders an image within a responsive container,
 * handling dynamic image loading and aspect ratios for both mobile and desktop devices.
 *
 * The component leverages a ref to the image element to measure its height upon load and invokes
 * the provided `onLoad` callback with the measured height. It also supports dynamic image widths,
 * accessibility attributes, and display priority for optimized rendering.
 *
 * Props:
 * @param {Object}   image            - The image source object to be rendered.
 * @param {boolean}  displayPriority  - If true, prioritizes loading of this image.
 * @param {string}   imageAlt         - Alternative text for the image for accessibility.
 * @param {string}   imageTitle       - Title attribute for the image.
 * @param {string}   title            - Optional title to be displayed or passed to the image.
 * @param {number}   ratioMobile      - Aspect ratio for the image on mobile devices.
 * @param {number}   ratioDesktop     - Aspect ratio for the image on desktop devices.
 * @param {Function} onLoad           - Callback function invoked with the image height after load.
 * @param {Object}   props            - Additional props spread onto the container Box.
 *
 * Usage:
 * This component is typically used within layout sections where responsive images are required,
 * and where the parent component needs to be notified of the image's rendered height for layout calculations.
 */
const ImageContainer = ({
    image,
    displayPriority,
    imageAlt,
    imageTitle,
    title,
    ratioMobile,
    ratioDesktop,
    onLoad,
    ...props
}) => {
    const imageRef = useRef(null)

    const handleImageLoad = () => {
        if (onLoad && imageRef?.current) {
            onLoad(imageRef?.current?.clientHeight)
        }
    }

    useEffect(() => {
        if (onLoad && imageRef.current && imageRef?.current?.clientHeight > 0) {
            onLoad(imageRef?.current?.clientHeight)
        }
    }, [imageRef?.current?.clientHeight])

    return (
        <Box position="relative" {...props}>
            <PDCImage
                currentImage={image}
                imageWidths={DYNAMIC_IMAGE_WIDTHS.halfandfullonsmalldevice}
                imageAlt={imageAlt}
                imageTitle={imageTitle}
                imageProps={{
                    height: '100%',
                    ref: imageRef,
                    onLoad: () => {
                        handleImageLoad()
                    }
                }}
                displayPriority={displayPriority}
                title={title}
                skeletonProps={{aspectRatio: [ratioMobile, null, ratioDesktop], width: '100%'}}
            />
        </Box>
    )
}

ImageContainer.propTypes = {
    image: PropTypes.object,
    displayPriority: PropTypes.bool,
    imageAlt: PropTypes.string,
    imageTitle: PropTypes.string,
    title: PropTypes.string,
    ratioMobile: PropTypes.string,
    ratioDesktop: PropTypes.string,
    onLoad: PropTypes.func
}
